import { CoinShop } from './CoinShop';
import { Profile } from './Profile';
import { Achievements } from './Achievements';

/**
 * Loadout / perk system — choose up to 2 passive perks before each run.
 * Perks unlock via profile level or achievements. Effects applied during gameplay.
 */
export class Perks {

    // Perk IDs
    static readonly THICK_SKIN: number = 0;    // 20% damage reduction
    static readonly SWIFT: number = 1;         // +1 starting speed
    static readonly FORTIFIED: number = 2;     // +25 starting health
    static readonly GLASS_CANNON: number = 3;  // half health, double score
    static readonly ADRENALINE: number = 4;    // dash cooldown 40% shorter
    static readonly SECOND_WIND: number = 5;   // shield recharges 50% faster
    static readonly SLOW_STARTER: number = 6;  // +1 slow-mo charge
    static readonly STREAK_MASTER: number = 7; // streak builds 30% faster
    static readonly PERK_COUNT: number = 8;

    static readonly MAX_EQUIPPED: number = 2;

    static readonly NAMES: string[] = [
        "Thick Skin", "Swift", "Fortified", "Glass Cannon",
        "Adrenaline", "Second Wind", "Slow Starter", "Streak Master"
    ];

    static readonly DESCRIPTIONS: string[] = [
        "Take 20% less damage from all sources",
        "+1 starting movement speed",
        "+25 starting health capacity",
        "Half health, but earn double score",
        "Dash cooldown is 40% shorter",
        "Shield recharges 50% faster",
        "+1 slow-motion charge at start",
        "Dodge streak builds 30% faster"
    ];

    // Icons — single character symbols for the perk cards
    static readonly ICONS: string[] = [
        "❤",  // heart
        "⇨",  // arrow right
        "⚔",  // crossed swords (shield)
        "☠",  // skull
        "↯",  // lightning
        "◔",  // circle with segment
        "⏱",  // stopwatch
        "★"   // star
    ];

    // Currently equipped perks for this run (-1 = empty)
    private static equipped: number[] = [-1, -1];

    // ===== Unlock conditions =====

    static isUnlocked(perk: number): boolean {
        if (CoinShop.isPerkUnlockedByShop(perk)) return true;
        switch (perk) {
            case Perks.THICK_SKIN:    return Profile.getLevel() >= 2;
            case Perks.SWIFT:         return true;
            case Perks.FORTIFIED:     return Profile.getLevel() >= 4;
            case Perks.GLASS_CANNON:  return Profile.getLevel() >= 8;
            case Perks.ADRENALINE:    return Profile.getLevel() >= 6;
            case Perks.SECOND_WIND:   return Profile.getLevel() >= 10;
            case Perks.SLOW_STARTER:  return Profile.getTotalBossesDefeated() >= 5;
            case Perks.STREAK_MASTER: return Achievements.getUnlockedCount() >= 10;
            default: return false;
        }
    }

    static getUnlockDesc(perk: number): string {
        switch (perk) {
            case Perks.THICK_SKIN:    return "Reach profile level 2";
            case Perks.SWIFT:         return ""; // always unlocked
            case Perks.FORTIFIED:     return "Reach profile level 4";
            case Perks.GLASS_CANNON:  return "Reach profile level 8";
            case Perks.ADRENALINE:    return "Reach profile level 6";
            case Perks.SECOND_WIND:   return "Reach profile level 10";
            case Perks.SLOW_STARTER:  return "Defeat 5 bosses";
            case Perks.STREAK_MASTER: return "Unlock 10 achievements";
            default: return "";
        }
    }

    // ===== Equip / Unequip =====

    static getEquipped(slot: number): number {
        return (slot >= 0 && slot < Perks.MAX_EQUIPPED) ? Perks.equipped[slot] : -1;
    }

    static isEquipped(perk: number): boolean {
        return Perks.equipped[0] === perk || Perks.equipped[1] === perk;
    }

    static toggleEquip(perk: number): void {
        if (!Perks.isUnlocked(perk)) return;

        // If already equipped, unequip
        for (let i = 0; i < Perks.MAX_EQUIPPED; i++) {
            if (Perks.equipped[i] === perk) {
                Perks.equipped[i] = -1;
                return;
            }
        }

        // Find empty slot
        for (let i = 0; i < Perks.MAX_EQUIPPED; i++) {
            if (Perks.equipped[i] === -1) {
                Perks.equipped[i] = perk;
                return;
            }
        }

        // Both slots full — replace the second one
        Perks.equipped[1] = perk;
    }

    static clearLoadout(): void {
        Perks.equipped[0] = -1;
        Perks.equipped[1] = -1;
    }

    static getEquippedCount(): number {
        let c = 0;
        if (Perks.equipped[0] >= 0) c++;
        if (Perks.equipped[1] >= 0) c++;
        return c;
    }

    // ===== Effect queries (used by gameplay code) =====

    static has(perk: number): boolean {
        return Perks.equipped[0] === perk || Perks.equipped[1] === perk;
    }

    /** Damage multiplier — applied to all incoming damage */
    static getDamageMultiplier(): number {
        let mult = 1;
        if (Perks.has(Perks.THICK_SKIN)) mult *= 0.8;
        if (Perks.has(Perks.GLASS_CANNON)) mult *= 1; // glass cannon doesn't increase damage, just halves max HP
        return mult;
    }

    /** Starting speed bonus */
    static getStartingSpeedBonus(): number {
        return Perks.has(Perks.SWIFT) ? 1 : 0;
    }

    /** Starting health bonus */
    static getStartingHealthBonus(): number {
        return Perks.has(Perks.FORTIFIED) ? 25 : 0;
    }

    /** Max health multiplier (Glass Cannon halves it) */
    static getMaxHealthMultiplier(): number {
        return Perks.has(Perks.GLASS_CANNON) ? 0.5 : 1;
    }

    /** Score multiplier from perks */
    static getScoreMultiplier(): number {
        return Perks.has(Perks.GLASS_CANNON) ? 2 : 1;
    }

    /** Dash cooldown multiplier */
    static getDashCooldownMultiplier(): number {
        return Perks.has(Perks.ADRENALINE) ? 0.6 : 1;
    }

    /** Shield cooldown multiplier */
    static getShieldCooldownMultiplier(): number {
        return Perks.has(Perks.SECOND_WIND) ? 0.5 : 1;
    }

    /** Extra slow-mo charges */
    static getExtraSlowmoCharges(): number {
        return Perks.has(Perks.SLOW_STARTER) ? 1 : 0;
    }

    /** Streak build speed multiplier */
    static getStreakSpeedMultiplier(): number {
        return Perks.has(Perks.STREAK_MASTER) ? 1.3 : 1;
    }
}
